import { useEffect, useState } from "react";

export function useScrollDetection({
  contentElementId,
  progressFillId,
  isEnabled,
}: {
  contentElementId: string;
  progressFillId: string;
  isEnabled: boolean;
}) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  useEffect(() => {
    if (!isEnabled) {
      setHasScrolledToBottom(true);
      return;
    }

    const contentElement = document.getElementById(contentElementId);
    if (!contentElement) return;

    const rmdWrapper = document.getElementById("rmd-v2-wrapper");
    // Once unlocked, never re-lock even if user scrolls back up.
    let unlocked = false;
    let rafId: number | null = null;

    // Progress bar is driven via direct DOM writes instead of React state.
    // Setting state on every scroll frame re-renders the whole step (including
    // the heavy ClientContentBlocks subtree); on Safari, reconciling the
    // scrolled overflow-auto container mid-scroll resets its scrollTop and the
    // page jumps. Writing style.width bypasses React entirely — no re-render.
    const setProgress = (pct: number) => {
      const fill = document.getElementById(progressFillId);
      if (!fill) return;
      fill.style.width = `${pct}%`;
      fill.classList.toggle("bg-green-500", pct >= 99);
      fill.classList.toggle("bg-remedium-blue", pct < 99);
    };

    const compute = () => {
      // Re-detect on every call — orientation/resize can change overflow-y
      const isContainerScroll =
        !!rmdWrapper &&
        (getComputedStyle(rmdWrapper).overflowY === "auto" ||
          getComputedStyle(rmdWrapper).overflowY === "scroll");

      let scrollTop: number;
      let scrollMax: number;

      if (isContainerScroll) {
        scrollTop = rmdWrapper.scrollTop;
        scrollMax = rmdWrapper.scrollHeight - rmdWrapper.clientHeight;
      } else {
        scrollTop = window.scrollY;
        scrollMax = document.documentElement.scrollHeight - window.innerHeight;
      }

      if (scrollMax <= 0) {
        setProgress(100);
        unlocked = true;
        setHasScrolledToBottom(true);
        return;
      }

      setProgress(Math.min((scrollTop / scrollMax) * 100, 100));

      const threshold = Math.min(200, scrollMax * 0.1);
      if (!unlocked && scrollTop >= scrollMax - threshold) {
        unlocked = true;
      }

      if (unlocked) {
        setHasScrolledToBottom(true);
      }
    };

    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        compute();
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    rmdWrapper?.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });
    window.addEventListener("orientationchange", handleScroll, {
      passive: true,
    });

    compute();

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", handleScroll);
      rmdWrapper?.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      window.removeEventListener("orientationchange", handleScroll);
    };
  }, [contentElementId, progressFillId, isEnabled]);

  return { hasScrolledToBottom };
}
